import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Garde-route pour les zones réservées aux comptes réels (Lab, Logs, Hub auth).
 *
 * Comportement :
 *   - utilisateur réel  → rend `children`
 *   - en cours de chargement → spinner
 *   - guest ou anonyme → panneau « Sign in to access » (jamais une mire d'auth
 *     plein écran : la mire ne s'ouvre que sur action explicite via le bouton).
 *
 * Le bouton « Sign in » dispatche `request_open_login_modal` que `App.js`
 * intercepte pour afficher l'AuthModal.
 */
const ProtectedRoute = ({ children }) => {
    const { t } = useTranslation();
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div
                style={{
                    minHeight: '100vh',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#111827',
                    color: '#93c5fd',
                    gap: '12px',
                }}
            >
                <LoadingSpinner size="large" />
                <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>{t('session_loading')}</p>
            </div>
        );
    }

    if (!isAuthenticated) {
        const requestLogin = () => {
            try {
                window.dispatchEvent(new CustomEvent('request_open_login_modal'));
            } catch (_) { /* ignore */ }
        };
        return (
            <div
                style={{
                    minHeight: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#111827',
                    color: '#e5e7eb',
                    gap: 18,
                    padding: 24,
                }}
            >
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>
                    {t('protected.title', { defaultValue: 'Sign in required' })}
                </h2>
                <p style={{ margin: 0, opacity: 0.85, textAlign: 'center', maxWidth: 420 }}>
                    {t('protected.description', {
                        defaultValue:
                            'This area is reserved for signed-in users. You can keep browsing free exams without an account.',
                    })}
                </p>
                <button
                    type="button"
                    onClick={requestLogin}
                    style={{
                        padding: '10px 20px',
                        borderRadius: 8,
                        border: '1px solid rgba(96, 165, 250, 0.45)',
                        background: 'rgba(59, 130, 246, 0.18)',
                        color: '#bfdbfe',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 500,
                    }}
                >
                    {t('login_button', { defaultValue: 'Sign in' })}
                </button>
            </div>
        );
    }

    return children;
};

export default ProtectedRoute;
